import { supabase } from "@/integrations/supabase/client";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

type DirectUploadParams = {
  bucket: string;
  path: string;
  file: File;
  cacheControl?: string;
};

function encodeStoragePath(path: string) {
  // Keep folder structure but encode each segment safely
  return path
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

async function getReadableUploadError(res: Response) {
  const text = await res.text();
  if (!text) return `Upload fehlgeschlagen (${res.status})`;

  try {
    const json = JSON.parse(text);
    return (
      json?.message ||
      json?.error_description ||
      json?.error ||
      json?.msg ||
      text
    );
  } catch {
    return text;
  }
}

/**
 * Direct upload to Lovable Cloud storage via fetch.
 * This avoids adding extra headers (e.g. x-client-info, x-upsert) that can cause failing CORS preflights in some environments.
 */
export async function directUploadToStorage({
  bucket,
  path,
  file,
  cacheControl = "3600",
}: DirectUploadParams) {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (!data.session?.access_token) throw new Error("Nicht angemeldet");

  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
    throw new Error("Backend-Konfiguration fehlt");
  }

  const formData = new FormData();
  formData.append("cacheControl", cacheControl);
  // Supabase Storage expects the file as a multipart field.
  // supabase-js uses an empty field name here.
  formData.append("", file);

  const url = `${SUPABASE_URL}/storage/v1/object/${bucket}/${encodeStoragePath(path)}`;

  const res = await fetch(url, {
    method: "POST",
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${data.session.access_token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    throw new Error(await getReadableUploadError(res));
  }
}
